import FetchStaffMembers from '@Application/Facilities/StaffMembers/StaffMembers/Queries/FetchStaffMembers';
import fetchStaffMembers from '@Application/Facilities/StaffMembers/StaffMembers/fetchStaffMembers';
import { PermissionEnum } from '@Application/Security/Roles';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import Filters from '@UI/Pages/Agencies/StaffMembers/Filters';
import staffMemberFiltersSignal, {
	setStaffMemberFilters,
} from '@UI/Pages/Agencies/StaffMembers/Filters/Signals/staffMemberFiltersSignal';
import StaffMemberFilterView from '@UI/Pages/Agencies/StaffMembers/Filters/StaffMemberFilterView';
import filterStaffMembers from '@UI/Pages/Agencies/StaffMembers/Helpers/filterStaffMembers';
import StaffMemberCard from '@UI/Pages/Agencies/StaffMembers/StaffMemberCard';
import { openStaffMemberSidebar } from '@UI/Pages/Agencies/StaffMembers/Signals/staffMemberSidebarSignal';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import type StaffMemberView from '@UI/Store/StaffMembers/Views/StaffMemberView';
import { css } from '@emotion/react';
import { css as toClassName } from '@emotion/css';
import { Plus, TriangleAlert, Users as UsersIcon } from 'lucide-react';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, DataView as StaffMembersDataView } from '@verclam/ui-kit';
import { theme } from '@Guidelines/Theme/theme';

const contentStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	flex: 1;
	overflow: hidden;
`;

const cardsGridStyle = toClassName`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0.875rem;
	margin-bottom: 0.875rem;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		grid-template-columns: 1fr;
	}
`;

const ROWS_PER_PAGE = 12;
const ROWS_PER_PAGE_OPTIONS = [12, 24, 48];

/**
 * Owns the fetch and every branch of the staff members list. Loads the organization's ENTIRE
 * collection through `getPaginated` (`fetchStaffMembers`, `page: 0`) — the server offers `search` /
 * `jobFunction` / `facilityId` query params, but this screen never sends them (D-16): every filter is
 * applied client-side, via `filterStaffMembers` on the full list.
 *
 * Branch order follows `OrganizationsContent.tsx`, not `FacilitiesContent.tsx`: `isLoading` reads
 * `false` on the very first render (the hook only flips it inside an effect), so a branch that tests
 * `error !== null` before `data === null && error === null` would flash the error state at every
 * mount. The request SHAPE, on the other hand — the guarded `query` function and `stopOn` — is
 * copied line for line from `FacilitiesContent.tsx`.
 */
const StaffMembersContent: React.FC = () => {
	const dispatch = useDispatch();
	const { currentOrganization } = useCurrentOrganization();
	const organizationId = currentOrganization?.id ?? null;
	const filters = useSignalValue(staffMemberFiltersSignal);

	const {
		data: staffMembersList,
		isLoading,
		refetch,
		error,
	} = useStoreQuery({
		selector: (state) => state.staffMembersStore.staffMembers,
		stopOn: (value) => value !== null && value.organizationId === organizationId,
		query: () => {
			if (organizationId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchStaffMembers(new FetchStaffMembers(organizationId)));
		},
		deps: [organizationId],
	});

	const filteredList = React.useMemo(
		() => (staffMembersList !== null ? filterStaffMembers(staffMembersList.list, filters) : []),
		[staffMembersList, filters],
	);

	const handleCreateClick = React.useCallback(() => {
		openStaffMemberSidebar(null);
	}, []);

	const handleResetFiltersClick = React.useCallback(() => {
		setStaffMemberFilters(StaffMemberFilterView.createDefault());
	}, []);

	const renderStaffMemberCard = React.useCallback(
		(staffMember: StaffMemberView) => <StaffMemberCard staffMember={staffMember} />,
		[],
	);

	const dataViewPt: React.ComponentProps<typeof StaffMembersDataView>['pt'] = React.useMemo(
		() => ({
			root: {
				style: {
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					gap: '1rem',
					overflow: 'hidden',
				},
			},
			grid: { className: cardsGridStyle },
			content: {
				style: {
					flex: 1,
					overflowY: 'auto',
				},
			},
			emptyMessage: {
				style: {
					height: '100%',
				},
			},
		}),
		[],
	);

	const emptyMessage = React.useMemo(() => {
		const style = {
			height: '100%',
		};

		if (isLoading) {
			return <div style={style} />;
		}

		if (error) {
			return (
				<ErrorState
					title="Impossible de charger le personnel"
					description="Une erreur est survenue pendant le chargement de la liste du personnel."
					onRetry={refetch}
				/>
			);
		}

		if (filters.hasActiveFilters()) {
			return (
				<EmptyState
					title="Aucun résultat"
					description={`La recherche porte sur l'agence ${currentOrganization?.name ?? '"inconnue"'} et sur les membres du personnel qui vous sont affectés.`}
					icon={<TriangleAlert size={28} />}
					action={
						<Button
							type="button"
							text
							label="Réinitialiser les filtres"
							onClick={handleResetFiltersClick}
						/>
					}
					style={style}
				/>
			);
		}

		return (
			<EmptyState
				title="Aucun membre du personnel"
				description="Créez le premier membre du personnel de cette structure."
				icon={<UsersIcon size={28} />}
				action={
					<WithPermission permission={PermissionEnum.STAFF_WRITE}>
						<Button
							type="button"
							label="Nouveau membre"
							icon={<Plus size={16} />}
							onClick={handleCreateClick}
						/>
					</WithPermission>
				}
			/>
		);
	}, [isLoading, filters, handleCreateClick, handleResetFiltersClick, currentOrganization, refetch, error]);

	if (organizationId === null) {
		return (
			<EmptyState
				title="Aucune structure"
				description="Vous n’êtes affecté à aucune structure : aucun membre du personnel ne peut être chargé."
				icon={<TriangleAlert size={28} />}
			/>
		);
	}

	return (
		<div css={contentStyle}>
			<Filters />
			<StaffMembersDataView
				emptyMessage={emptyMessage}
				value={filteredList}
				layout="grid"
				dataKey="id"
				itemTemplate={renderStaffMemberCard}
				loading={isLoading}
				pt={dataViewPt}
				paginator
				rows={ROWS_PER_PAGE}
				rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
			/>
		</div>
	);
};

export default StaffMembersContent;
